<template>
  <div>
    <v-dialog
      v-model="show_access_code_dialog"
      persistent
      :overlay-opacity="1"
      overlay-color="secondary"
      :max-width="400"
    >
      <v-card>
        <v-card-title class="blue white--text">Access restricted</v-card-title>
        <v-card-text class="py-6">
          <v-text-field
            v-model="access_code"
            type="text"
            id="portal-password"
            label="Enter access code"
            autocomplete="off"
            hide-details
            v-on:keyup.enter="load"
          ></v-text-field>

          <div class="text-center pt-3">
            <span v-if="access_code_invalid" class="error--text"
              >Invalid access code</span
            >
          </div>
        </v-card-text>
        <v-card-actions class="grey lighten-4">
          <v-btn text to="/">Go Home</v-btn>
          <v-spacer></v-spacer>
          <v-btn color="blue" dark @click="load" :disabled="!access_code"
            >Submit</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog
      v-model="show_error_dialog"
      persistent
      :overlay-opacity="1"
      overlay-color="secondary"
      :max-width="400"
    >
      <v-card>
        <v-card-title class="primary white--text">
          Portal unavailable
          <v-spacer></v-spacer>
          <v-icon dark>fa-exclamation-triangle</v-icon>
        </v-card-title>
        <v-card-text class="py-6">
          <div class="text-center pt-3">
            <h3 class>{{ error_message }}</h3>
          </div>
        </v-card-text>
        <v-card-actions class="grey lighten-4">
          <v-spacer></v-spacer>
          <v-btn text to="/">Go Home</v-btn>
          <v-spacer></v-spacer>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-overlay absolute :opacity="0.75" v-if="loading">
      <div class="text-center">
        <v-progress-circular :size="100" indeterminate></v-progress-circular>
        <div class="caption mt-4">Loading</div>
      </div>
    </v-overlay>

    <transition name="slide-x-reverse-transition">
      <v-container v-if="!loading && portal">
        <v-row justify="center">
          <v-col cols="11" sm="10" md="9" lg="8" xl="6">
            <v-container id="portal-info">
              <div>
                <v-row justify="center" style="position: relative">
                  <v-col
                    cols="12"
                    sm="11"
                    md="10"
                    lg="9"
                    xl="7"
                    class="text-center"
                  >
                    <div>
                      <img
                        :title="portal.logo.name"
                        :src="portal.logo.url"
                        style="width: 100%; max-width: 375px; height: auto"
                        @click="$emit('logo-upload')"
                      />
                    </div>
                  </v-col>
                  <v-col cols="12" v-if="portal.video">
                    <video
                      playsinline
                      style="
                        width: 100%;
                        height: auto;
                        max-height: 66vh;
                        margin-bottom: -8px;
                      "
                      :src="portal.video.url"
                      :poster="portal.video.thumbnail"
                      controls
                    ></video>
                  </v-col>
                </v-row>
              </div>

              <div style="position: relative">
                <div style="position: absolute; top: -10px; right: 0px">
                  <div v-if="download_link">
                    <v-btn
                      id="download-btn"
                      :href="download_link"
                      target="_blank"
                      class="mt-2"
                      outlined
                      :dark="isDark"
                      >Download ready</v-btn
                    >
                  </div>
                  <div v-else-if="portal.downloads">
                    <v-btn
                      title="Download files"
                      icon
                      :dark="isDark"
                      :loading="generating_zip"
                      @click="downloadZip"
                    >
                      <v-icon>fa-file-download</v-icon>
                    </v-btn>
                    <br />
                    <span
                      v-if="generating_zip"
                      :class="`ml-2 caption ${isDark ? 'white--text' : ''}`"
                      >Please wait...</span
                    >
                  </div>
                </div>
                <v-row align="center">
                  <v-col cols="12">
                    <div v-if="portal.title || portal.description">
                      <h4>{{ portal.title }}</h4>
                      <p class="caption">{{ portal.description }}</p>
                    </div>
                  </v-col>
                </v-row>
              </div>
            </v-container>

            <portal-applicants
              :items="portal.applicants"
              v-model="selected_applicant"
              v-if="portal.applicants.length"
            ></portal-applicants>
            <v-container v-else>
              <v-row>
                <v-col cols="12" class="text-center">
                  <v-icon x-large class="mb-4">fa-hourglass-half</v-icon>
                  <h4>This portal has no applicants yet</h4>
                </v-col>
              </v-row>
            </v-container>

            <v-container v-if="selected_applicant">
              <div id="applicant-info">
                <v-row justify="center">
                  <v-col cols="12" class="py-2" v-if="selected_applicant.name">
                    <v-text-field
                      dense
                      outlined
                      hide-details
                      name="name"
                      label="Name"
                      :value="selected_applicant.name"
                      readonly
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" class="py-2" v-if="selected_applicant.email">
                    <v-text-field
                      dense
                      outlined
                      hide-details
                      readonly
                      name="email"
                      label="Email"
                      type="email"
                      :value="selected_applicant.email"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" class="py-2" v-if="selected_applicant.phone">
                    <v-text-field
                      dense
                      outlined
                      hide-details
                      name="phone"
                      label="Phone #"
                      type="tel"
                      readonly
                      :value="selected_applicant.phone"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" v-if="selected_applicant.notes">
                    <v-card>
                      <v-card-title class="cyan white--text py-0">
                        <h5>Notepad</h5>
                      </v-card-title>
                      <v-textarea
                        hide-details
                        dense
                        filled
                        :rows="$vuetify.breakpoint.name === 'xs' ? 6 : 4"
                        @change="onChange"
                        v-model="selected_applicant.notes"
                        style="font-size: 13px"
                      ></v-textarea>
                    </v-card>
                  </v-col>
                </v-row>
              </div>

              <div id="portal-sections">
                <v-row>
                  <v-col
                    cols="12"
                    v-for="(section, s) in selected_applicant.sections"
                    :key="s"
                  >
                    <portal-section
                      v-if="
                        section.videos.files.length +
                          section.photos.files.length +
                          section.documents.files.length >
                        0
                      "
                      :applicant="selected_applicant"
                      :value="section"
                      :readonly="true"
                      :portal="portal"
                      :downloads="portal.downloads"
                    ></portal-section>
                  </v-col>
                </v-row>
              </div>
            </v-container>
          </v-col>
        </v-row>
      </v-container>
    </transition>
  </div>
</template>

<script>
import portalSection from "@/components/portal/applicant-view/section";
import portalApplicants from "@/components/portal/applicants";

export default {
  name: "PortalView",
  components: {
    portalSection,
    portalApplicants,
  },
  data() {
    return {
      loading: false,
      access_code: "",
      access_code_required: false,
      access_code_invalid: false,
      show_access_code_dialog: false,
      error_message: "",
      show_error_dialog: false, //Shows a popup saying the portal is unavailable. (user account is bad or portal isnt active)
      portal: null,
      selected_applicant: null,
      generating_zip: false,
      download_link: null,
    };
  },
  computed: {},
  created() {
    this.load();
  },
  methods: {
    async downloadZip() {
      this.generating_zip = true;
      try {
        //Get job, keep checking id until available.
        let zip_job_id = await this.$api({
          url: `/viewer/portal/${
            this.portal.uid || this.portal.viewer_uid
          }/download`,
          method: "GET",
        });
        let zip_link;
        while (!zip_link) {
          await this.$helpers.sleep(2000);
          let zip_job = await this.$api(`/download/${zip_job_id}`);
          if (zip_job.status == "done") {
            zip_link = zip_job.link;
          }
        }
        this.download_link = zip_link;

        setTimeout(
          function () {
            this.download_link = null;
          }.bind(this),
          120000
        );
        setTimeout(
          function () {
            document.querySelector("#download-btn").click();
          }.bind(this),
          1000
        );
      } catch (ex) {
        alert("There was a problem generating a zip file. Please try again.");
      }
      this.generating_zip = false;
    },
    async load() {
      this.loading = true;
      this.access_code_invalid = false;

      try {
        let response = await this.$api(
          `/viewer/portal/${this.$route.params.uid}?access_code=${this.access_code}`
        );
        if (response.access_code_required) {
          this.access_code_required = true;
          this.show_access_code_dialog = true;
          if (response.access_code_invalid) {
            this.access_code = "";
            this.access_code_invalid = true;
          }
        } else {
          this.portal = response;

          this.show_access_code_dialog = false;
        }
      } catch (ex) {
        this.error_message = ex.error;
        this.show_error_dialog = true;
      }
      this.loading = false;
    },
  },
};
</script>

<style lang="scss" scoped>
#portal-applicants {
  overflow-y: auto;
  overflow-x: hidden;
  $box-height: 36px;
  @media only screen and (max-width: 600px) {
    /* xs */
    max-height: $box-height * 3;
  }
  @media only screen and (max-width: 960px) and (min-width: 600px) {
    /* sm */
    max-height: $box-height * 3;
  }
  @media only screen and (max-width: 1264px) and (min-width: 960px) {
    /* md */
    max-height: $box-height * 3;
  }
  @media only screen and (max-width: 1904px) and (min-width: 1264px) {
    /* lg */
    max-height: $box-height * 3;
  }
  @media only screen and (min-width: 1904px) {
    /* xl */
    max-height: $box-height * 3;
  }
}
/* width */
::-webkit-scrollbar {
  width: 10px;
}

/* Track */
::-webkit-scrollbar-track {
  box-shadow: inset 0 0 5px grey;
  border-radius: 10px;
}

/* Handle */
::-webkit-scrollbar-thumb {
  background: grey;
  border-radius: 10px;
}

/* Handle on hover */
::-webkit-scrollbar-thumb:hover {
  background: grey;
}
</style>